/**
 * BusBooking.jsx
 *
 * Post-payment landing page for bus bookings.
 * Mirrors FligthBooking.js exactly — same modal stages, same polling pattern.
 *
 * Route: /book-bus/status
 * Receives state: { rc_booking_id, userContact, userToken, source, searchQuery }
 * OR reads rc_booking_id from localStorage / URL query param.
 */

import React, { useEffect, useState } from "react";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";

import ProcessingModal from "../components/modal/ProcessingModal";
import FailureModal from "../components/modal/FailureModal";
import BookingFailedModal from "../components/modal/BookingFailedModal";
import BookingReviewError from "../components/bookingFlight/BookingReviewError";
import BusBookingConfirmation from "../components/bookingBus/BusBookingConfirmation";

import { getBusBookingStatus } from "../services/busBookingAPI";
import "../../src/components/componetsChat/style/skeleton.css";
import { useBookingLeadSignals, toISODate } from "../components/lead-bot";

const BusBooking = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { openLoginModal } = useAuth();

  const [isLoading, setIsLoading]                   = useState(true);
  const [modalStage, setModalStage]                 = useState("validating");
  const [showBookingDetails, setShowBookingDetails] = useState(false);
  const [ticketData, setTicketData]                 = useState(null);
  const [errorMessage, setErrorMessage]             = useState("");
  const [currentStatus, setCurrentStatus]           = useState("");
  const [minTimePassed, setMinTimePassed]           = useState(false);

  // searchQuery built from booking data for Go Back prefill
  const [builtSearchQuery, setBuiltSearchQuery]     = useState(
    location.state?.searchQuery || ""
  );

  // Minimum 2 s on "Validating" so it doesn't flash
  useEffect(() => {
    const t = setTimeout(() => setMinTimePassed(true), 2000);
    return () => clearTimeout(t);
  }, []);

  // validating → booking once payment confirmed and min time elapsed
  useEffect(() => {
    if (
      modalStage === "validating" &&
      minTimePassed &&
      ["PAYMENT_SUCCESS", "TICKET_ISSUED", "SEAT_BLOCKED",
       "BOOKING_IN_PROGRESS", "SUCCESS"].includes(currentStatus)
    ) {
      setModalStage("booking");
    }
  }, [modalStage, minTimePassed, currentStatus]);

  // booking → show confirmation once ticket data arrives
  useEffect(() => {
    if (modalStage === "booking" && ticketData) {
      const t = setTimeout(() => {
        setModalStage("none");
        setShowBookingDetails(true);
        setIsLoading(false);
      }, 1000);
      return () => clearTimeout(t);
    }
  }, [modalStage, ticketData]);

  // Block back-nav during critical stages
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (["validating", "booking"].includes(modalStage)) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    const handlePopState = () => {
      if (["validating", "booking"].includes(modalStage)) {
        window.history.pushState(null, "", window.location.href);
        alert("Please do not go back while your booking is being processed.");
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);
    window.history.pushState(null, "", window.location.href);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [modalStage]);

  // ── Polling loop ──────────────────────────────────────────────────────────────
  useEffect(() => {
    let pollingTimer;
    let attempts = 0;
    const MAX_ATTEMPTS = 60; // ~3 minutes at 3 s interval

    const poll = async () => {
      const stateData   = location.state || {};
      const rcBookingId =
        localStorage.getItem("rc_bus_booking_id") ||
        searchParams.get("rc_booking_id") ||
        stateData.rc_booking_id;

      if (!rcBookingId) {
        setModalStage("none");
        navigate("/");
        return;
      }

      // Resolve auth — mirrors FligthBooking.js pattern
      const user = auth.currentUser;
      let userToken   = stateData.userToken;
      let userContact = user?.email || user?.phoneNumber || stateData.userContact || "";

      if (user && !userToken) {
        userToken = await user.getIdToken().catch(() => null);
      }
      if (!userToken) {
        const stored = localStorage.getItem("userToken");
        if (stored) userToken = stored;
      }
      if (!userContact) {
        userContact =
          localStorage.getItem("userEmail") ||
          localStorage.getItem("email") ||
          "";
      }

      try {
        attempts++;

        const response = await getBusBookingStatus({
          rcBookingId,
          userToken,
          userContact,
        });

        const booking = response?.data?.bookings?.[0] ||
                        response?.data?.booking       ||
                        response?.bookings?.[0];
        const status  = booking?.status;
        const message = response?.message;

        setCurrentStatus(status);

        // ── Terminal: success ──────────────────────────────────────────────────
        if (status === "TICKET_ISSUED" || status === "SUCCESS") {
          toast.success(message || "Bus ticket booked successfully!", {
            position: "top-center",
          });

          // Parse any stringified sub-fields
          let processed = { ...booking };
          if (typeof processed.ticket_details === "string") {
            try { processed.ticket_details = JSON.parse(processed.ticket_details); } catch {}
          }
          if (typeof processed.booking_request === "string") {
            try { processed.booking_request = JSON.parse(processed.booking_request); } catch {}
          }
          if (typeof processed.boarding_point === "string") {
            try { processed.boarding_point = JSON.parse(processed.boarding_point); } catch {}
          }
          if (typeof processed.dropping_point === "string") {
            try { processed.dropping_point = JSON.parse(processed.dropping_point); } catch {}
          }

          setTicketData(processed);
          localStorage.removeItem("rc_bus_booking_id");
          return;
        }

        // ── Terminal: payment failed ───────────────────────────────────────────
        if (status === "PAYMENT_FAILED" || status === "FAILED") {
          toast.error(message || "Payment failed.", { position: "top-center" });
          setErrorMessage(message || "Payment verification failed.");
          setModalStage("payment_failed");
          setIsLoading(false);
          return;
        }

        // ── Terminal: ticket failed ────────────────────────────────────────────
        if (status === "TICKET_FAILED" || status === "ORDER_FAILED") {
          toast.error(message || "Booking failed.", { position: "top-center" });

          // Prefer the original search URL (stored in state when user clicked Book Bus)
          // so Go Back restores the exact search that led to this booking.
          const stateSearchUrl  = stateData?.searchUrl || '';
          const bookingSearchUrl = booking?.search_url || '';

          if (stateSearchUrl) {
            // searchUrl from navigate state is already a relative query string e.g. "?from=...&modes=Bus"
            setBuiltSearchQuery(stateSearchUrl.startsWith('?') ? stateSearchUrl : `?${stateSearchUrl}`);
          } else if (bookingSearchUrl) {
            // search_url stored in DB may be a full URL — strip host
            try {
              const rel = new URL(bookingSearchUrl).search;
              setBuiltSearchQuery(rel || bookingSearchUrl);
            } catch {
              setBuiltSearchQuery(bookingSearchUrl);
            }
          } else {
            // Last resort: reconstruct from stored city names
            const src  = booking?.source_name      || "";
            const dest = booking?.destination_name || "";
            const date = booking?.journey_date     || "";
            if (src && dest) {
              setBuiltSearchQuery(
                `?from=${encodeURIComponent(src)}&to=${encodeURIComponent(dest)}&date=${date}&modes=Bus`
              );
            }
          }

          setTicketData(booking);
          setModalStage("booking_failed");
          setIsLoading(false);
          return;
        }

        // ── Continue polling ───────────────────────────────────────────────────
        if (attempts < MAX_ATTEMPTS) {
          pollingTimer = setTimeout(poll, 3000);
        } else {
          setErrorMessage(
            "Verification is taking longer than expected. Check 'My Bookings' in a few minutes."
          );
          setModalStage("payment_failed");
          setIsLoading(false);
        }

      } catch (error) {
        console.warn("[BusBooking] Polling error:", error);

        const is404 =
          error?.response?.status === 404 ||
          String(error).includes("404") ||
          error?.message?.includes("not found");

        if (is404) {
          setErrorMessage(
            "We couldn't retrieve your booking. If you were charged, please contact support."
          );
          setModalStage("payment_failed");
          setIsLoading(false);
          return;
        }

        const isAuth =
          error?.status === 401 ||
          error?.response?.status === 401;

        if (isAuth) {
          setModalStage("error");
          setErrorMessage("Authentication required to verify your booking.");
          setIsLoading(false);
          openLoginModal();
          return;
        }

        if (attempts < MAX_ATTEMPTS) {
          pollingTimer = setTimeout(poll, 3000);
        } else {
          setErrorMessage(
            "Verification is taking longer than expected. Check 'My Bookings' in a few minutes."
          );
          setModalStage("payment_failed");
          setIsLoading(false);
        }
      }
    };

    // Wait for auth to initialise on first load (mirrors FligthBooking.js)
    const start = async () => {
      if (!auth.currentUser) {
        await new Promise((resolve) => {
          const timeout = setTimeout(() => resolve(null), 3500);
          const unsub = auth.onAuthStateChanged((u) => {
            clearTimeout(timeout);
            unsub();
            resolve(u);
          });
        });
      }
      poll();
    };

    start();
    return () => clearTimeout(pollingTimer);
  }, [searchParams, location.state, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Lead bot (Routy): only speaks up when the booking went wrong ─────────────
  const failedStage = ["payment_failed", "booking_failed"].includes(modalStage);
  // Timeout / not found: payment may have gone through, so don't call it a payment failure
  const verifyIssue = modalStage === "payment_failed"
    && /longer than expected|couldn't retrieve/i.test(errorMessage || "");
  const rcIdForLead = ticketData?.rc_booking_id
    || searchParams.get("rc_booking_id")
    || location.state?.rc_booking_id
    || "";
  useBookingLeadSignals({
    booking: {
      mode: "bus",
      stage: "status",
      src_name: ticketData?.source_name || "",
      dest_name: ticketData?.destination_name || "",
      travel_date: toISODate(ticketData?.journey_date),
      amount: Number(ticketData?.final_total_with_discounts || ticketData?.final_total_fare_with_taxes || 0) || null,
      rcBookingId: rcIdForLead || null,
      prefillPhone: auth.currentUser?.phoneNumber || "",
      key: `bus:${rcIdForLead || "status"}`,
    },
    stage: "status",
    paymentFailed: modalStage === "payment_failed" && !verifyIssue,
    bookingFailed: modalStage === "booking_failed" || verifyIssue,
    suppress: { notFailed: !failedStage },
  });

  // ── Render ────────────────────────────────────────────────────────────────────
  const stateData = location.state || {};
  const source    = stateData.source || "search";

  return (
    <>
      {/* ── Modals ─────────────────────────────────────────────────────────── */}
      {modalStage === "validating" && (
        <ProcessingModal
          title="Validating Payment"
          message="We are confirming your transaction with the bank."
        />
      )}

      {modalStage === "booking" && (
        <ProcessingModal
          title="Booking Your Bus"
          message="Payment confirmed! We are reserving your seats now."
        />
      )}

      {modalStage === "payment_failed" && (
        <FailureModal
          title={
            errorMessage?.includes("couldn't retrieve")
              ? "Booking Not Found"
              : "Payment Failed"
          }
          errorMessage={errorMessage}
          retryLabel={
            errorMessage?.includes("couldn't retrieve") ? "Back to Home" : "Try Again"
          }
          onRetry={() => {
            if (errorMessage?.includes("couldn't retrieve")) {
              navigate(source === "search" ? `/search-form${builtSearchQuery}` : "/");
            } else {
              window.location.reload();
            }
          }}
          onContactSupport={() => navigate("/chat")}
        />
      )}

      {modalStage === "booking_failed" && (
        <BookingFailedModal
          bookingId={ticketData?.rc_booking_id}
          ticketData={ticketData}
          source={source}
          searchQuery={builtSearchQuery}   // prefills search on Go Back
          onClose={() => setModalStage("none")}
        />
      )}

      {modalStage === "error" && (
        <BookingReviewError
          errorMessage={errorMessage}
          onRetry={() => window.location.reload()}
          onSignIn={() => openLoginModal()}
          source={source}
          searchQuery={builtSearchQuery}
        />
      )}

      {/* ── Main content (blurred while modal is up) ────────────────────────── */}
      <div
        className={`mt-16 min-h-screen bg-[#F6F8FA] transition-all duration-500 ${
          modalStage !== "none" ? "blur-md pointer-events-none" : ""
        }`}
      >
        <main className="mx-auto max-w-[1200px] px-4 md:px-6 py-4 md:py-6 space-y-4 md:space-y-6">
          {showBookingDetails && ticketData ? (
            <BusBookingConfirmation
              bookingData={ticketData}
              onContactSupport={() => navigate("/chat")}
            />
          ) : (
            <>
              {/* Skeleton while loading */}
              <section className="bg-white rounded-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.06)] p-4 md:p-6 space-y-4">
                <div className="mx-auto h-28 w-[90%] rounded-[12px] shimmer" />
                <div className="bg-[#F7F9FB] rounded-[12px] h-28 md:h-32 shimmer" />
              </section>
              <section className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
                <div className="md:col-span-7">
                  <div className="bg-white rounded-[12px] shadow-sm p-4 h-64 shimmer" />
                </div>
                <div className="md:col-span-5">
                  <div className="bg-white rounded-[12px] shadow-sm p-4 h-64 shimmer" />
                </div>
              </section>
            </>
          )}
        </main>
      </div>
    </>
  );
};

export default BusBooking;
